/**
 * Contenu du recu de la vente de secours (design 4.5), sans mise en page :
 * des lignes de texte que RecuImprimable.jsx pose sur 80 mm. Fonctions
 * pures : la vente (VenteEnregistree du contrat) et le magasin suffisent.
 *
 * Rien n'est recalcule : les montants sont ceux de la vente (totaux du panier
 * et paiement), formates par formatMontant du contrat.
 */
import { TAUX_TAXES, formatMontant } from '../vente/contrat';

export const MENTION_SECOURS = 'VENTE DE SECOURS (HORS LIGNE)';
export const NUMERO_A_FOURNIR = '[à fournir]';
export const PIED_RETOURS = 'Échanges et retours dans Lightspeed après le retour du système.';

/** Taux en millioniemes -> « 9,975 % ». */
export function libelleTaux(ppm) {
    return `${String(ppm / 10000).replace('.', ',')} %`;
}

/** Montant signe : « +0,02 $ », « -0,02 $ », « 0,00 $ ». */
export function montantSigne(cents) {
    return cents > 0 ? `+${formatMontant(cents)}` : formatMontant(cents);
}

/** « 2026-11-03T14:22:05.120-05:00 » -> « 2026-11-03 14:22 » (heure locale du poste). */
export function dateRecu(iso) {
    const s = String(iso || '');
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) ? `${s.slice(0, 10)} ${s.slice(11, 16)}` : s;
}

function texte(v) {
    return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

/** Lignes d'un texte libre du magasin (en-tete ou pied), vides retirees. */
function lignesLibres(v) {
    const t = texte(v);
    return t ? t.split(/\r?\n/).map((l) => l.trim()).filter(Boolean) : [];
}

function poids(weightG) {
    return `${(weightG / 1000).toFixed(3).replace('.', ',')} kg`;
}

function libelleRabais(discount) {
    if (!discount) return 'Rabais';
    const valeur = discount.type === 'POURCENT'
        ? `${String(discount.valeur / 100).replace('.', ',')} %`
        : formatMontant(discount.valeur);
    const qui = discount.par && texte(discount.par.display_name);
    return `Rabais ${valeur}${qui ? ` (${qui})` : ''}`;
}

/** Une ligne d'article du recu : libelle, detail (quantite ou poids), montant, rabais. */
function article(l) {
    let detail = null;
    if (l.mode === 'POIDS') {
        detail = l.weight_g ? `${poids(l.weight_g)} x ${formatMontant(l.unit_price_cents)}/kg` : 'Au poids';
    } else if (l.qty > 1) {
        detail = `${l.qty} x ${formatMontant(l.unit_price_cents)}`;
    }
    return {
        line_no: l.line_no,
        libelle: l.name,
        detail,
        montant: formatMontant(l.gross_cents),
        prix_modifie: l.override && l.override.par && texte(l.override.par.display_name)
            ? `Prix modifié (${texte(l.override.par.display_name)})`
            : null,
        rabais: l.discount_cents > 0 ? { libelle: libelleRabais(l.discount), montant: formatMontant(-l.discount_cents) } : null,
    };
}

/**
 * @param {object} vente VenteEnregistree
 * @param {{ store?: object, config?: object, terminal?: object }} magasin contexteRecu.js
 * @returns {object} recu en lignes de texte
 */
export function construireRecu(vente, magasin = {}) {
    const store = magasin.store || {};
    const config = magasin.config || {};
    const nomMagasin = texte(store.name);
    const raisonSociale = texte(config.receipt_legal_name) || nomMagasin || 'Raison sociale [à fournir]';
    const entete = [raisonSociale.toUpperCase()];
    if (nomMagasin && nomMagasin.toLowerCase() !== raisonSociale.toLowerCase()) entete.push(nomMagasin);
    for (const v of [texte(store.address), texte(store.phone)]) if (v) entete.push(v);
    entete.push(...lignesLibres(config.receipt_header));

    const t = vente.totals;
    const p = vente.payment;
    return {
        entete,
        mention: `** ${MENTION_SECOURS} **`,
        numero: `No ${vente.local_number}`,
        date: dateRecu(vente.completed_at),
        caissier: `Caissier : ${vente.employee_display_name}`,
        articles: vente.lines.map(article),
        totaux: [
            { libelle: 'Sous-total', montant: formatMontant(t.subtotal_cents) },
            { libelle: `TPS ${libelleTaux(TAUX_TAXES.TPS_PPM)}`, montant: formatMontant(t.tps_cents) },
            { libelle: `TVQ ${libelleTaux(TAUX_TAXES.TVQ_PPM)}`, montant: formatMontant(t.tvq_cents) },
            { libelle: 'TOTAL', montant: formatMontant(t.total_cents), fort: true },
            { libelle: 'Arrondi comptant', montant: montantSigne(p.cash_rounding_cents) },
            { libelle: 'À payer', montant: formatMontant(p.amount_due_cents), fort: true },
            { libelle: 'Comptant', montant: formatMontant(p.cash_received_cents) },
            { libelle: 'Monnaie', montant: formatMontant(p.change_given_cents) },
        ],
        articles_compte: `${t.item_count} article${t.item_count > 1 ? 's' : ''}`,
        taxes: [
            `TPS : ${texte(config.tps_number) || NUMERO_A_FOURNIR}`,
            `TVQ : ${texte(config.tvq_number) || NUMERO_A_FOURNIR}`,
        ],
        pied: [PIED_RETOURS, ...lignesLibres(config.receipt_footer)],
        reimpression: vente.receipt_print_count > 0 ? 'COPIE (réimpression)' : null,
    };
}
